import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /**
   * What will happen, in plain words. A text is one paragraph; nodes (a few short `<p>`s)
   * are one block. Either way the dialog is described by it (`aria-describedby`), so a
   * screen reader hears the consequences along with the title.
   */
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** The red confirm button. `false` (with your own `confirmLabel`) for an action that mails or notifies someone but deletes nothing. */
  destructive?: boolean
  /** A request is running: neither button works, and Escape, the scrim and the close button do nothing. */
  busy?: boolean
  confirmDisabled?: boolean
  /** Gets the trimmed reason when `reasonLabel` is set, otherwise ''. */
  onConfirm: (reason: string) => void
  /** Runs on the cancel button only, not on Escape (say, to jump to the findings); the dialog closes either way. */
  onCancel?: () => void
  /** Asks for a reason: this is its label, and confirming stays disabled until one is typed. */
  reasonLabel?: string
  reasonPlaceholder?: string
  /** Why the last attempt failed, above the buttons. */
  error?: string | null
  /** Controls the caller needs (a choice, a field). They sit under the description, outside it. */
  children?: ReactNode
}

/**
 * The one confirmation pattern for destructive or wide actions (IA-12, ED-51): bulk,
 * merging, deleting a category that is in use and, since spec-65, the actions that mail
 * someone or are hard to undo (DD goedkeuren, IC-besluit wijzigen, wasstraat afronden,
 * afgeronde taken archiveren). Never `window.confirm`.
 *
 * The cancel button comes before the confirm button, so it is the default focus and Enter
 * never confirms by accident. With `reasonLabel` the reason field comes first and takes the
 * focus; Enter in a textarea only adds a line, so the safe default holds either way. What was
 * typed belongs to one opening: Radix unmounts the content when the dialog closes, and the
 * next opening starts empty.
 *
 * When it closes, the focus goes back to what had it when the dialog opened, if that is
 * still there. Radix only does this for its own `DialogTrigger`, and this dialog is opened
 * through `open` (a state, or conditional rendering), so without this the focus would drop
 * to the page and a keyboard user would start again from the top.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Verwijderen',
  cancelLabel = 'Annuleren',
  destructive = true,
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  reasonLabel,
  reasonPlaceholder,
  error = null,
  children,
}: ConfirmDialogProps) {
  const opener = useRef<HTMLElement | null>(null)
  // A layout effect, so it reads the focus before the dialog takes it: Radix mounts the
  // content after this render and moves the focus into it in a passive effect.
  useLayoutEffect(() => {
    if (!open) return
    const active = document.activeElement
    opener.current = active instanceof HTMLElement && active !== document.body ? active : null
  }, [open])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Escape, the scrim and the close button all end up here, and none of them may
        // cut off a request that is running.
        if (!busy) onOpenChange(next)
      }}
    >
      <DialogContent
        size="sm"
        {...(description ? {} : { 'aria-describedby': undefined })}
        onCloseAutoFocus={(event) => {
          // Replaces Radix's own focus return, which has no trigger to go back to.
          event.preventDefault()
          opener.current?.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <Description>{description}</Description> : null}
        </DialogHeader>
        {children}
        <ConfirmControls
          onOpenChange={onOpenChange}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          destructive={destructive}
          busy={busy}
          confirmDisabled={confirmDisabled}
          onConfirm={onConfirm}
          onCancel={onCancel}
          reasonLabel={reasonLabel}
          reasonPlaceholder={reasonPlaceholder}
          error={error}
        />
      </DialogContent>
    </Dialog>
  )
}

/** A text stays the one paragraph it always was; nodes become one block of paragraphs. */
function Description({ children }: { children: ReactNode }) {
  if (typeof children === 'string') return <DialogDescription>{children}</DialogDescription>
  return (
    <DialogDescription asChild>
      <div className="flex flex-col gap-2">{children}</div>
    </DialogDescription>
  )
}

type ConfirmControlsProps = Pick<
  ConfirmDialogProps,
  'onOpenChange' | 'onConfirm' | 'onCancel' | 'reasonLabel' | 'reasonPlaceholder'
> & {
  confirmLabel: string
  cancelLabel: string
  destructive: boolean
  busy: boolean
  confirmDisabled: boolean
  error: string | null
}

/**
 * The reason field, the error and the buttons: the part of the dialog with state of its
 * own, so it mounts and unmounts with the content.
 */
function ConfirmControls({
  onOpenChange,
  confirmLabel,
  cancelLabel,
  destructive,
  busy,
  confirmDisabled,
  onConfirm,
  onCancel,
  reasonLabel,
  reasonPlaceholder,
  error,
}: ConfirmControlsProps) {
  const [reason, setReason] = useState('')
  const reasonId = useId()
  const needsReason = reasonLabel !== undefined
  const canConfirm = !busy && !confirmDisabled && (!needsReason || reason.trim() !== '')

  return (
    <>
      {needsReason ? (
        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor={reasonId} className="text-sm font-medium text-foreground">
            {reasonLabel}
          </label>
          <Textarea
            id={reasonId}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            // What the IC endpoint takes for a change reason (`change_reason`, 2000).
            maxLength={2000}
            placeholder={reasonPlaceholder}
            className="w-full resize-none"
          />
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => {
            onCancel?.()
            onOpenChange(false)
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          disabled={!canConfirm}
          onClick={() => onConfirm(reason.trim())}
        >
          {busy ? 'Bezig…' : confirmLabel}
        </Button>
      </DialogFooter>
    </>
  )
}
